import CryptoJS from 'crypto-js';
const CDN_DOMAIN = 'https://media.spyne.ai';

// Field written by syndication-smartview next to each image field in the media
// JSON it dumps to S3: { [imageField]: signature }.
// The signature is Thumbor's signature of the bare image (bucket/key). The media
// CDN accepts it with any approved size and filters inserted after it, so the
// URL for every resolution is assembled here with no key involved.
// Older dumps may carry { [imageField]: { [resolution]: signature } } instead;
// those signatures are only valid for the resolution they were made for.
const SIGNATURE_FIELD = 'thumbor_sig';
const SIGNATURE_FORMAT = 'webp';
const ANY_RESOLUTION = '*';
const MAX_WALK_DEPTH = 8;

// Kill switch for the rollout: REACT_APP_USE_CDN_SIGNATURES=false ignores the
// dumped signatures and keeps the previous behaviour (browser signing).
const USE_CDN_SIGNATURES = String(process.env.REACT_APP_USE_CDN_SIGNATURES || 'true').toLowerCase() !== 'false';

// original image URL -> { '*': signature } or { [resolution]: signature }, filled from dumped JSON.
const signatureRegistry = new Map();

function parseS3Url(url) {
    if (!url || typeof url !== 'string') return null;
    try {
        // Accept both ...s3.amazonaws.com/... and ...s3.<region>.amazonaws.com/...
        const s3Regex = /^([a-zA-Z0-9\-]+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
        const parsed = new URL(url);
        const host = parsed.hostname || '';
        const key = (parsed.pathname || '').replace(/^\/+/, '');
        if (!key) return null;
        const s3Match = s3Regex.exec(host);
        if (s3Match) {
            return { bucket: s3Match[1], key };
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Thumbor operation path, identical on the backend signer:
 * `{resolution}/smart/filters:format({format})/{imagePath}`.
 */
function buildOperationPath(imagePath, resolution, filterFormat) {
    const parts = [];

    parts.push(resolution || '0x0');

    parts.push('smart');
    if (filterFormat) {
        parts.push(`filters:format(${filterFormat})`);
    }
    parts.push(imagePath);
    return parts.join('/');
}

function assembleCdnUrl(signature, operationPath) {
    return `${CDN_DOMAIN}/${signature}/${operationPath}`;
}

function getSecureThumborUrl(imagePath, resolution, filterFormat, secretKey) {
    const operationPath = buildOperationPath(imagePath, resolution, filterFormat);

    const signature = CryptoJS.HmacSHA1(operationPath, secretKey);
    const base64 = CryptoJS.enc.Base64.stringify(signature);
    const urlSafe = base64.replaceAll('+', '-').replaceAll('/', '_');

    return assembleCdnUrl(urlSafe, operationPath);
}


function getUnsafeThumborUrl(imagePath, resolution, filterFormat) {
    const baseUrl = process.env.REACT_APP_PUBLIC_IMAGE_COMPRESSOR_BASE_URL || `${CDN_DOMAIN}/unsafe`;
    const parts = [baseUrl.replace(/\/+$/, '')];
    if (resolution) parts.push(resolution);
    parts.push(`filters:format(${filterFormat})`, imagePath);
    return parts.join('/');
}

/**
 * True when the URL is already a signed URL on our CDN, so it must be used
 * as-is and never wrapped into another Thumbor path.
 */
function isSignedCdnUrl(url) {
    return typeof url === 'string' && url.startsWith(`${CDN_DOMAIN}/`) && !url.startsWith(`${CDN_DOMAIN}/unsafe/`);
}

function rememberSignatures(original, value) {
    const existing = signatureRegistry.get(original) || {};
    let added = false;
    if (typeof value === 'string' && value) {
        existing[ANY_RESOLUTION] = value;
        added = true;
    } else if (value && typeof value === 'object') {
        Object.keys(value).forEach((resolution) => {
            if (typeof value[resolution] === 'string' && value[resolution]) {
                existing[resolution] = value[resolution];
                added = true;
            }
        });
    }
    if (added) signatureRegistry.set(original, existing);
    return added;
}

/**
 * Walk media JSON (catalog / spin / threed / hero payloads, or any wrapper
 * around them) and remember every backend signature by its original URL.
 * Safe to call with anything; unknown shapes are simply ignored.
 *
 * @param {*} payload - dumped JSON, postMessage data, or an array of them
 * @returns {number} how many original URLs now have signatures registered
 */
export const registerCdnSignatures = (payload) => {
    let registered = 0;
    const walk = (node, depth) => {
        if (!node || typeof node !== 'object' || depth > MAX_WALK_DEPTH) return;
        if (Array.isArray(node)) {
            node.forEach((item) => walk(item, depth + 1));
            return;
        }
        const signatures = node[SIGNATURE_FIELD];
        if (signatures && typeof signatures === 'object') {
            Object.keys(signatures).forEach((field) => {
                const original = node[field];
                if (typeof original !== 'string' || !original) return;
                if (rememberSignatures(original, signatures[field])) registered += 1;
            });
        }
        Object.keys(node).forEach((key) => {
            if (key !== SIGNATURE_FIELD) walk(node[key], depth + 1);
        });
    };
    try {
        walk(payload, 0);
    } catch (error) {
        console.error('registerCdnSignatures failed:', error);
    }
    return registered;
};

/**
 * Backend signature for this original URL and resolution, if the dumped JSON
 * carried one: the per-image signature serves every resolution, a per-resolution
 * signature only its own.
 */
export const getRegisteredSignature = (url, resolution, filterFormat = SIGNATURE_FORMAT) => {
    if (!USE_CDN_SIGNATURES || typeof url !== 'string' || filterFormat !== SIGNATURE_FORMAT) return null;
    const entry = signatureRegistry.get(url);
    if (!entry) return null;
    const signature = entry[ANY_RESOLUTION] || entry[resolution || '0x0'];
    return typeof signature === 'string' && signature ? signature : null;
};

export const clearCdnSignatureRegistry = () => {
    signatureRegistry.clear();
};

export const IMAGE_COMPRESSOR = (props) => {
    const SECRET_KEY = process.env.REACT_APP_THUMBOR_SECRET_KEY || process.env.THUMBOR_API_KEY;
    const { url, resolution, filterFormate, isOriginal = false } = props;

    if (isOriginal) {
        return url;
    }

    // Already a signed CDN URL: use as-is, never wrap it again.
    if (isSignedCdnUrl(url)) {
        return url;
    }

    const filterFormat = filterFormate || 'webp';

    const s3 = parseS3Url(url);
    const imagePath = s3 ? `${s3.bucket}/${s3.key}` : url;

    // 1. Prefer the signature the backend wrote into the dumped JSON (no key needed).
    const signature = getRegisteredSignature(url, resolution, filterFormat);
    if (signature) {
        return assembleCdnUrl(signature, buildOperationPath(imagePath, resolution, filterFormat));
    }

    // 2. Fallback: sign in the browser (older data without thumbor_sig).
    if (SECRET_KEY) {
        return getSecureThumborUrl(imagePath, resolution, filterFormat, SECRET_KEY);
    }

    return getUnsafeThumborUrl(imagePath, resolution, filterFormat);
};
